import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Layers } from 'lucide-react';
import { 
  FaTrash, 
  FaPlus, 
  FaServer, 
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaEye,
  FaCopy,
  FaSearch,
  FaSort
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import CreateImageModal from './CreateImageModal';
import ImageDetailsModal from './ImageDetailsModal';
import { useNotification } from '../../Notifications';
import { useAuth } from '../../components/auth/AuthContext';

import Sidebar from '../../components/layout/Sidebar';
import { useResponsive } from '../../components/vm/useResponsive';
import * as Sentry from "@sentry/react";

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'image-page' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'image-page' },
      extra: context 
    });
  }
};

// Image Icon component
const ImageIcon = ({ size = 'medium' }) => {
  let width, height;
  
  if (size === 'large') {
    width = '48px';
    height = '48px';
  } else if (size === 'medium') {
    width = '40px';
    height = '40px';
  } else if (size === 'small') {
    width = '20px';
    height = '20px';
  } else {
    width = '40px';
    height = '40px';
  }
  
  return (
    <div style={{ width, height, position: 'relative', flexShrink: 0 }}>
      <svg viewBox="0 0 24 24" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
        <rect
          x="2" y="4" width="20" height="16" rx="2"
          fill="#1b2b1a"
          stroke="#e0eacc"
          strokeWidth="1"
        />
        <path
          d="M4 16 L10 12 L14 14 L20 10 L20 16 L4 16 Z"
          fill="#e0eacc"
          fillOpacity="0.2"
        />
        <circle
          cx="8" cy="8" r="2"
          fill="#e0eacc"
        />
      </svg>
    </div>
  );
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'image-page',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

const ERROR_MESSAGES = {
  'AUTH_ERROR': 'Please log in again to continue',
  'SESSION_EXPIRED': 'Session expired. Please log in again.',
  'IMAGE_LIST_FAILED': 'Unable to load images. Please refresh the page.',
  'IMAGE_CREATE_FAILED': 'Unable to create image. Please try again.',
  'IMAGE_DELETE_FAILED': 'Unable to delete image. Please try again.',
  'IMAGE_DETAILS_FAILED': 'Unable to load image details. Please try again.',
  'NO_SNAPSHOTS_AVAILABLE': 'No snapshots available for image creation.',
  'USER_NOT_FOUND': 'Please refresh and try again',
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable',
  'NETWORK_ERROR': 'Unable to connect. Please check your connection.',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.',
  'REQUEST_TOO_LARGE': 'Request size too large. Please reduce the data size.',
  'RESOURCE_IN_USE': 'This resource is currently in use and cannot be modified.',
  'INVALID_ID_FORMAT': 'Invalid request. Please try again',
  'INVALID_DATA': 'Invalid input data',
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'DB_ERROR': 'Service temporarily unavailable',
  'INVALID_PARAMETERS': 'Invalid parameters provided. Please check your input and try again',
  'INVALID_REQUEST': 'Invalid request format. Please try again',
  'INVALID_USER_PROFILE': 'Invalid user profile format',
  'INCOMPLETE_PROFILE': 'Please complete your profile to continue',
  'UNEXPECTED_PAYLOAD': 'Invalid request format',
  'INVALID_JSON': 'Invalid request format',
  'API_ERROR': 'Service temporarily unavailable. Please try again later',
  'VALIDATION_ERROR': 'Please check your input and try again',
  'IMAGE_ALREADY_EXISTS_FOR_SNAPSHOT': 'An image already exists for this snapshot. Please delete the existing image first',
  'INVALID_IMAGE_PARAMETERS': 'Please check your image name and try again',
  'SNAPSHOT_NOT_FOUND': 'Selected snapshot is not available or you do not have permission to access it',
  'IMAGE_NOT_FOUND': 'Image not found or you do not have permission to access it',
  // Network Errors (NEW)
'OFFLINE': "You're offline. Check your connection.",
'TIMEOUT': "Request timed out. Please try again.",
'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
'NO_RESPONSE': "No response from server. Please try again.",
'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again."
};

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Security: Safe error message function
const getSafeErrorMessage = (err) => {
  if (!err || typeof err !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  // Known error code from mapping
  if (err.code && ERROR_MESSAGES[err.code]) {
    return ERROR_MESSAGES[err.code];
  }
  
  // Interceptor's user-friendly message
  if (err.error) {
    return err.error;
  }
  
  return 'Something went wrong. Please try again.';
};

const getNetworkErrorMessage = (error) => {
  if (!navigator.onLine) {
    return { message: "You're offline. Check your connection.", code: 'OFFLINE' };
  }
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return { message: "Request timed out. Please try again.", code: 'TIMEOUT' };
  }
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return { message: "Unable to reach server. Please try later.", code: 'SERVER_UNREACHABLE' };
  }
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return { message: "You're offline. Check your connection.", code: 'OFFLINE' };
  }
  if (error.request && !error.response) {
    return { message: "No response from server. Please try again.", code: 'NO_RESPONSE' };
  }
  return { message: "Something went wrong. Please try again.", code: 'UNKNOWN_NETWORK_ERROR' };
};

// Performance: Debounce function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// Group pagination items per page
const GROUP_ITEMS_PER_PAGE = 15;

// Group Pagination Component
const GroupPagination = ({ currentPage, totalPages, onPageChange, totalItems }) => {
  if (totalPages <= 1) return null;
  
  const startItem = (currentPage - 1) * GROUP_ITEMS_PER_PAGE + 1;
  const endItem = Math.min(currentPage * GROUP_ITEMS_PER_PAGE, totalItems);
  
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 16px',
      backgroundColor: '#141414',
      borderTop: '1px solid #2a2a2a'
    }}>
      <span style={{ fontSize: '12px', color: '#6b7280' }}>
        Showing {startItem}-{endItem} of {totalItems}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            backgroundColor: currentPage === 1 ? '#1a1a1a' : '#333',
            color: currentPage === 1 ? '#444' : '#fff',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
          }}
        >
          <FaChevronLeft size={10} />
        </button>
        
        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            style={{
              minWidth: '28px',
              height: '28px',
              padding: '0 6px',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '12px',
              backgroundColor: currentPage === page ? '#e0eacc' : '#252525',
              color: currentPage === page ? '#000' : '#9ca3af',
              fontWeight: currentPage === page ? '600' : 'normal'
            }}
          >
            {page}
          </button>
        ))}
        
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            backgroundColor: currentPage === totalPages ? '#1a1a1a' : '#333',
            color: currentPage === totalPages ? '#444' : '#fff',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
          }}
        >
          <FaChevronRight size={10} />
        </button>
      </div>
    </div>
  );
};

// Delete Confirmation Modal Component
// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, itemName, isDeleting }) => {
  if (!isOpen) return null;
 
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#121212',
        border: '1px solid #333333',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #2a2a2a'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#ff7777">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
            </svg>
            <h3 style={{
              fontWeight: '600',
              color: '#fff',
              margin: 0,
              fontSize: '16px'
            }}>
              Delete Image
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isDeleting}
            style={{
              padding: '8px',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#6b7280',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '20px' }}>
          <p style={{
            color: '#9ca3af',
            marginBottom: '16px',
            marginTop: 0,
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            Are you sure you want to delete this image?
          </p>
          
          {/* Image Name Box */}
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            padding: '12px 14px',
            marginBottom: '16px'
          }}>
            <span style={{ color: '#6b7280', fontSize: '11px', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>
              Image Name
            </span>
            <span style={{ color: '#e0eacc', fontSize: '14px', fontWeight: '500' }}>
              {sanitizeText(itemName)}
            </span>
          </div>
          
          {/* Warning Box */}
          <div style={{
            backgroundColor: 'rgba(255,119,119,0.05)',
            border: '1px solid rgba(255,119,119,0.2)',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#ff8080">
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
            </svg>
            <span style={{ color: '#ff8080', fontSize: '12px' }}>
              This action cannot be undone.
            </span>
          </div>
          
          {/* Delete Button */}
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            style={{
              width: '100%',
              backgroundColor: isDeleting ? '#4a3030' : 'rgba(255,100,100,0.15)',
              border: '1px solid rgba(255,119,119,0.4)',
              color: '#ff7777',
              borderRadius: '8px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px 20px',
              fontSize: '14px',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              gap: '8px'
            }}
          >
            {isDeleting ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid transparent',
                  borderTopColor: '#ff7777',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Deleting...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                </svg>
                Delete Image
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
const ImagePage = () => {
  const { isMobile, isTablet } = useResponsive();
  const notification = useNotification();
  const { handleAuthError } = useAuth();
 
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [loadingStates, setLoadingStates] = useState({
    images: false,
    snapshots: false,
    creating: false,
    deleting: new Set(),
  });

  const [images, setImages] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [regions] = useState(['CANADA-1']);
  const [selectedRegion, setSelectedRegion] = useState('CANADA-1');
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, image: null });
  
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // New list view states
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [expandedImageId, setExpandedImageId] = useState(null);
  const [groupPages, setGroupPages] = useState({ 'All Images': 1 });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  
  const operationRefs = useRef({
    creating: new Set(),
    deleting: new Set()
  });
  
  const maxRetries = 3;
  const retryAttempts = useRef(0);

  const regionDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);
  const notificationRef = useRef(notification);
const handleAuthErrorRef = useRef(handleAuthError);
useEffect(() => {
  notificationRef.current = notification;
  handleAuthErrorRef.current = handleAuthError;
});
  
  const navigate = useNavigate();

  // Sort options
  const sortOptions = [
    { id: 'name', label: 'Name' },
    { id: 'size', label: 'Size' },
    { id: 'created', label: 'Date' },
  ];

  // Copy to clipboard helper
  const copyToClipboard = useCallback((id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    logUserAction('copy_image_id', { imageId: id });
  }, []);

  // Toggle group collapse
  const toggleGroup = useCallback((groupName) => {
    setCollapsedGroups(prev => ({ 
      ...prev, 
      [groupName]: !prev[groupName] 
    }));
    logUserAction('toggle_image_group', { 
      groupName, 
      collapsed: !collapsedGroups[groupName] 
    });
  }, [collapsedGroups]);

  // Handle group page change
  const handleGroupPageChange = useCallback((groupName, page) => {
    setGroupPages(prev => ({ ...prev, [groupName]: page }));
    logUserAction('group_page_change', { groupName, page });
  }, []);

  // Toggle expanded image row
  const handleToggleExpand = useCallback((imageId) => {
    setExpandedImageId(prev => prev === imageId ? null : imageId);
    logUserAction('toggle_image_expand', {
      imageId,
      expanded: expandedImageId !== imageId
    });
  }, [expandedImageId]);

  useEffect(() => {
    Sentry.setContext("image-page", {
      imageCount: images.length,
      selectedRegion,
      snapshotCount: snapshots.length,
      isMobile,
      isTablet
    });
  }, [images.length, selectedRegion, snapshots.length, isMobile, isTablet]);



  // Filtered and sorted images
  const filteredImages = useMemo(() => {
    let result = images.filter(img => img.region_name === selectedRegion);
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(img => 
        (img.name || '').toLowerCase().includes(query) ||
        (img.id || '').toLowerCase().includes(query)
      );
    }
    
    result = [...result].sort((a, b) => {
      if (sortBy === 'name') {
        return (a.name || '').localeCompare(b.name || '');
      }
      if (sortBy === 'size') {
        return (b.size || 0) - (a.size || 0);
      }
      if (sortBy === 'created') {
        const dateA = new Date(a.created_at || a.created || 0);
        const dateB = new Date(b.created_at || b.created || 0);
        return dateB - dateA;
      }
      return 0;
    });
    
    return result;
  }, [images, selectedRegion, searchQuery, sortBy]);

  // Grouped images
  const groupedImages = useMemo(() => {
    return { 'All Images': filteredImages };
  }, [filteredImages]);

  // Paginated images per group
  const getPaginatedGroupImages = useCallback((groupName) => {
    const groupImgs = groupedImages[groupName] || [];
    const currentPage = groupPages[groupName] || 1;
    const startIndex = (currentPage - 1) * GROUP_ITEMS_PER_PAGE;
    const endIndex = startIndex + GROUP_ITEMS_PER_PAGE;
    return groupImgs.slice(startIndex, endIndex);
  }, [groupedImages, groupPages]);

  // Get total pages for a group
  const getGroupTotalPages = useCallback((groupName) => {
    const groupImgs = groupedImages[groupName] || [];
    return Math.ceil(groupImgs.length / GROUP_ITEMS_PER_PAGE);
  }, [groupedImages]);


const debouncedFetchImages = useMemo(
  () => debounce(async () => {
    const startTime = performance.now();
    
    logInfo('Initiating image fetch request', { selectedRegion });

    if (retryAttempts.current >= maxRetries) {
      const errorMsg = 'Maximum refresh attempts reached. Please wait before trying again.';
      notificationRef.current.error(errorMsg);
      logError('Maximum image fetch attempts exceeded', {
        attempts: retryAttempts.current,
        maxRetries,
        selectedRegion
      });
      return;
    }
    
    retryAttempts.current++;
    setLoadingStates(prev => ({ ...prev, images: true }));
    
    try {
      const response = await api.getCustomImages();
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (response.success) {
        let extractedImages = [];
        if (response.images && Array.isArray(response.images)) {
          extractedImages = response.images;
        }
        
        setImages(extractedImages);
        
        if (!initialDataLoaded) setInitialDataLoaded(true);
        retryAttempts.current = 0;

        logPerformance('fetch_images', duration, {
          totalExtracted: extractedImages.length,
          selectedRegion,
          success: true
        });

        logInfo('Images loaded successfully', {
          totalExtracted: extractedImages.length,
          selectedRegion,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        const safeMessage = getSafeErrorMessage(response);
        notificationRef.current.error(safeMessage);
        setImages([]);

        logError('Failed to fetch images', {
          response: response || 'No response',
          selectedRegion,
          duration: `${duration.toFixed(2)}ms`
        });
      }
    } catch (err) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Position 1: Authentication error
      if (err.status === 401 || err.code === 'SESSION_EXPIRED' || err.code === 'AUTH_ERROR') {
        handleAuthErrorRef.current();
        return;
      }
      
      // Position 2: Network errors (interceptor sets code, no status)
      const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
      if (networkErrorCodes.includes(err.code)) {
        notificationRef.current.error(err.error);
        logError('Network error in debouncedFetchImages', { 
          code: err.code, 
          message: err.error,
          online: navigator.onLine,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        return;
      }
      
      // Position 3: Rate limit
      if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        notificationRef.current.error(err.error || 'Too many requests. Please wait before trying again.');
        return;
      }
      
      // Position 4: Known error codes
      if (err.code && ERROR_MESSAGES[err.code]) {
        notificationRef.current.error(ERROR_MESSAGES[err.code]);
        logError('Images fetch error with code', {
          code: err.code,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        return;
      }
      
      // Position 5: Server errors
      if (err.status >= 500 || err.code === 'SERVER_ERROR' || err.code === 'SERVICE_UNAVAILABLE') {
        notificationRef.current.error(err.error || 'Service temporarily unavailable. Please try again later.');
        return;
      }
      
      // Position 6: Fallback
      notificationRef.current.error(err.error || 'Something went wrong. Please try again.');

      logError('Error fetching images', {
        code: err.code,
        status: err.status,
        message: err.error,
        selectedRegion,
        duration: `${duration.toFixed(2)}ms`
      }, err);
    } finally {
      setLoadingStates(prev => ({ ...prev, images: false }));
    }
  }, 300),
  [selectedRegion, initialDataLoaded]
);
const debouncedFetchSnapshots = useMemo(
  () => debounce(async () => {
    const startTime = performance.now();
    
    logInfo('Initiating snapshot fetch request for images', { selectedRegion });

    setLoadingStates(prev => ({ ...prev, snapshots: true }));
    
    try {
      const response = await api.getSnapshots();
      const endTime = performance.now();
      const duration = endTime - startTime;
      
  if (response.success) {
  const relationships = response.relationships || {};
  const filteredSnapshots = (response.snapshots || []).filter(snapshot => {
    if (snapshot.region) {
      return snapshot.region.startsWith(selectedRegion.split('-')[0]);
    }
    
    if (snapshot.vm_environment && snapshot.vm_environment.region) {
      return snapshot.vm_environment.region.startsWith(selectedRegion.split('-')[0]);
    }
    
    return false;
  }).map(snapshot => ({
    ...snapshot,
    has_image: relationships[snapshot.snapshot_id]?.has_image || false,
    image: relationships[snapshot.snapshot_id]?.image || null
  }));
  
  setSnapshots(filteredSnapshots);

        logPerformance('fetch_snapshots_for_images', duration, {
          totalSnapshots: (response.snapshots || []).length,
          filteredCount: filteredSnapshots.length,
          selectedRegion,
          success: true
        });

        logInfo('Snapshots for images loaded successfully', {
          totalSnapshots: (response.snapshots || []).length,
          filteredCount: filteredSnapshots.length,
          selectedRegion,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        const safeMessage = getSafeErrorMessage(response);
        notificationRef.current.error(safeMessage);

        logError('Failed to fetch snapshots for images', {
          response: response || 'No response',
          selectedRegion,
          duration: `${duration.toFixed(2)}ms`
        });
      }
    } catch (err) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Position 1: Authentication error
      if (err.status === 401 || err.code === 'SESSION_EXPIRED' || err.code === 'AUTH_ERROR') {
        handleAuthErrorRef.current();
        return;
      }
      
      // Position 2: Network errors
      const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
      if (networkErrorCodes.includes(err.code)) {
        notificationRef.current.error(err.error);
        logError('Network error in debouncedFetchSnapshots', { 
          code: err.code, 
          message: err.error,
          online: navigator.onLine,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        return;
      }
      
      // Position 3: Rate limit
      if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        notificationRef.current.error(err.error || 'Too many requests. Please wait before trying again.');
        return;
      }
      
      // Position 4: Known error codes
      if (err.code && ERROR_MESSAGES[err.code]) {
        notificationRef.current.error(ERROR_MESSAGES[err.code]);
        logError('Snapshots fetch error with code', {
          code: err.code,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        return;
      }
      
      // Position 5: Server errors
      if (err.status >= 500 || err.code === 'SERVER_ERROR' || err.code === 'SERVICE_UNAVAILABLE') {
        notificationRef.current.error(err.error || 'Service temporarily unavailable. Please try again later.');
        return;
      }
      
      // Position 6: Fallback
      notificationRef.current.error(err.error || 'Something went wrong. Please try again.');

      logError('Error fetching snapshots for images', {
        code: err.code,
        status: err.status,
        message: err.error,
        selectedRegion,
        duration: `${duration.toFixed(2)}ms`
      }, err);
    } finally {
      setLoadingStates(prev => ({ ...prev, snapshots: false }));
    }
  }, 300),
  [selectedRegion]
);

  useEffect(() => {
    logInfo('ImagePage component mounted or region changed', {
      isMobile,
      isTablet,
      selectedRegion
    });
    
    debouncedFetchImages();
    debouncedFetchSnapshots();
  }, [selectedRegion, debouncedFetchImages, debouncedFetchSnapshots]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(event.target)) {
        setIsRegionDropdownOpen(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setSortDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset group pages when region or search changes
  useEffect(() => {
    setGroupPages({ 'All Images': 1 });
    setExpandedImageId(null);
  }, [selectedRegion, searchQuery, sortBy]);
const handleCreateImage = useCallback(async (snapshotId, imageData) => {
  if (operationRefs.current.creating.has(snapshotId)) {
    return;
  }
  
  const startTime = performance.now();
  
  operationRefs.current.creating.add(snapshotId);
  
  logUserAction('create_image_initiated', {
    snapshotId,
    imageName: imageData.name,
    hasDescription: !!imageData.description
  });

  logInfo('Starting image creation', {
    snapshotId,
    imageName: imageData.name,
    hasDescription: !!imageData.description
  });

  setLoadingStates(prev => ({ ...prev, creating: true }));
  
  try {
    const response = await api.createImageFromSnapshot(snapshotId, imageData);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response.success) {
      setCreateModalOpen(false);
      notification.success('Image created successfully');
      debouncedFetchImages();

      logPerformance('create_image', duration, {
        snapshotId,
        imageName: imageData.name,
        imageId: response.image_id || 'pending',
        success: true
      });

      logInfo('Image created successfully', {
        snapshotId,
        imageName: imageData.name,
        imageId: response.image_id || 'pending',
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('create_image_success', {
        snapshotId,
        imageName: imageData.name,
        imageId: response.image_id || 'pending'
      });
    } else {
      const safeMessage = getSafeErrorMessage(response);
      notification.error(safeMessage);

      logError('Failed to create image', {
        snapshotId,
        imageName: imageData.name,
        response: response || 'No response',
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('create_image_failed', {
        snapshotId,
        imageName: imageData.name
      });
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'SESSION_EXPIRED' || err.code === 'AUTH_ERROR') {
      handleAuthError();
      return;
    }
    
    // Position 2: Network errors
    const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
    if (networkErrorCodes.includes(err.code)) {
      notification.error(err.error);
      logError('Network error in handleCreateImage', { 
        code: err.code, 
        message: err.error,
        online: navigator.onLine,
        duration: `${duration.toFixed(2)}ms`,
        snapshotId,
        imageName: imageData.name
      }, err);
      return;
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      notification.error(err.error || 'Too many requests. Please wait before trying again.');
      return;
    }
    
    // Position 4: Known error codes
    if (err.code && ERROR_MESSAGES[err.code]) {
      notification.error(ERROR_MESSAGES[err.code]);
      logError('Create image error with code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 5: Server errors
    if (err.status >= 500 || err.code === 'SERVER_ERROR' || err.code === 'SERVICE_UNAVAILABLE') {
      notification.error(err.error || 'Service temporarily unavailable. Please try again later.');
      return;
    }
    
    // Position 6: Fallback
    notification.error(err.error || 'Something went wrong. Please try again.');

    logError('Error creating image', {
      snapshotId,
      imageName: imageData.name,
      code: err.code,
      status: err.status,
      message: err.error,
      duration: `${duration.toFixed(2)}ms`
    }, err);

    logUserAction('create_image_error', {
      snapshotId,
      imageName: imageData.name
    });
  } finally {
    operationRefs.current.creating.delete(snapshotId);
    setLoadingStates(prev => ({ ...prev, creating: false }));
  }
}, [debouncedFetchImages, handleAuthError, notification]);

  const handleDeleteClick = useCallback((image) => {
    if (operationRefs.current.deleting.has(image.id) || loadingStates.deleting.has(image.id)) {
      return;
    }
    
    logUserAction('delete_image_modal_opened', {
      imageId: image.id,
      imageName: image.name
    });

    logInfo('Opening delete image modal', {
      imageId: image.id,
      imageName: image.name
    });
    
    setDeleteModal({
      open: true,
      image: image
    });
  }, [loadingStates.deleting]);
  
const handleDeleteConfirm = useCallback(async () => {
  if (!deleteModal.image) return;
  
  const imageId = deleteModal.image.id;
  const imageName = deleteModal.image.name;
  
  if (operationRefs.current.deleting.has(imageId)) {
    return;
  }
  
  operationRefs.current.deleting.add(imageId);
  
  const startTime = performance.now();
  
  logUserAction('delete_image_confirmed', {
    imageId,
    imageName
  });

  logInfo('Starting image deletion', {
    imageId,
    imageName
  });
  
  setIsDeleting(true);
  setLoadingStates(prev => ({ 
    ...prev, 
    deleting: new Set([...prev.deleting, imageId])
  }));
  
  try {
    const response = await api.deleteImage(imageId);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response.success) {
      notification.success('Image deleted successfully');
      debouncedFetchImages();

      logPerformance('delete_image', duration, {
        imageId,
        imageName,
        success: true
      });

      logInfo('Image deleted successfully', {
        imageId,
        imageName,
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('delete_image_success', {
        imageId,
        imageName
      });
    } else {
      const safeMessage = getSafeErrorMessage(response);
      notification.error(safeMessage);

      logError('Failed to delete image', {
        imageId,
        imageName,
        response: response || 'No response',
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('delete_image_failed', {
        imageId,
        imageName
      });
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Special case: 404 means already deleted
    if (err.status === 404 || err.code === 'IMAGE_NOT_FOUND' || err.code === 'RESOURCE_NOT_FOUND') {
      notification.success('Image deleted successfully');
      debouncedFetchImages();
      logInfo('Image already deleted, treating as success', {
        imageId,
        imageName,
        duration: `${duration.toFixed(2)}ms`
      });
      return;
    }
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'SESSION_EXPIRED' || err.code === 'AUTH_ERROR') {
      handleAuthError();
      return;
    }
    
    // Position 2: Network errors
    const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
    if (networkErrorCodes.includes(err.code)) {
      notification.error(err.error);
      logError('Network error in handleDeleteConfirm', { 
        code: err.code, 
        message: err.error,
        online: navigator.onLine,
        duration: `${duration.toFixed(2)}ms`,
        imageId,
        imageName
      }, err);
      return;
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      notification.error(err.error || 'Too many requests. Please wait before trying again.');
      return;
    }
    
    // Position 4: Known error codes
    if (err.code && ERROR_MESSAGES[err.code]) {
      notification.error(ERROR_MESSAGES[err.code]);
      logError('Delete image error with code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 5: Server errors
    if (err.status >= 500 || err.code === 'SERVER_ERROR' || err.code === 'SERVICE_UNAVAILABLE') {
      notification.error(err.error || 'Service temporarily unavailable. Please try again later.');
      return;
    }
    
    // Position 6: Fallback
    notification.error(err.error || 'Something went wrong. Please try again.');

    logError('Error deleting image', {
      imageId,
      imageName,
      code: err.code,
      status: err.status,
      message: err.error,
      duration: `${duration.toFixed(2)}ms`
    }, err);

    logUserAction('delete_image_error', {
      imageId,
      imageName
    });
  } finally {
    setDeleteModal({ open: false, image: null });
    setIsDeleting(false);
    operationRefs.current.deleting.delete(imageId);
    setLoadingStates(prev => {
      const newDeleting = new Set(prev.deleting);
      newDeleting.delete(imageId);
      return { ...prev, deleting: newDeleting };
    });
  }
}, [deleteModal.image, debouncedFetchImages, handleAuthError, notification]);
  const handleRegionChange = useCallback((region) => {
    logUserAction('region_changed', {
      fromRegion: selectedRegion,
      toRegion: region
    });

    logInfo('Region changed', {
      fromRegion: selectedRegion,
      toRegion: region,
      currentImageCount: images.length,
      currentSnapshotCount: snapshots.length
    });
    
    setSelectedRegion(region);
    setIsRegionDropdownOpen(false);
    setGroupPages({ 'All Images': 1 });
    setExpandedImageId(null);
    setSearchQuery('');
  }, [selectedRegion, images.length, snapshots.length]);
  
  const toggleRegionDropdown = useCallback((e) => {
    e.stopPropagation();
    const newState = !isRegionDropdownOpen;
    
    logUserAction('region_dropdown_toggled', {
      opened: newState
    });
    
    setIsRegionDropdownOpen(newState);
  }, [isRegionDropdownOpen]);

  const handleRefresh = useCallback(async () => {
    if (retryAttempts.current >= maxRetries) {
      const errorMsg = 'Maximum refresh attempts reached. Please wait before trying again.';
      notification.error(errorMsg);
      logError('Maximum refresh attempts exceeded', {
        attempts: retryAttempts.current,
        maxRetries,
        selectedRegion
      });
      return;
    }
    
    retryAttempts.current++;
    
    logUserAction('refresh_images', {
      attempt: retryAttempts.current,
      maxRetries,
      selectedRegion
    });

    logInfo('Refreshing images', {
      attempt: retryAttempts.current,
      maxRetries,
      currentImageCount: images.length,
      currentSnapshotCount: snapshots.length,
      selectedRegion
    });
    
    try {
      await debouncedFetchImages();
      retryAttempts.current = 0;
      logInfo('Image refresh completed successfully');
    } catch (err) {
      if (retryAttempts.current >= maxRetries) {
        const safeMessage = getSafeErrorMessage(err);
        notification.error(safeMessage);
        logError('Image refresh failed after max retries', {
          attempts: retryAttempts.current,
          maxRetries,
          selectedRegion
        }, err);
      }
    }
  }, [debouncedFetchImages, images.length, snapshots.length, selectedRegion, notification]);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      return 'N/A';
    }
  };

  const formatSize = (sizeInBytes) => {
    if (sizeInBytes === undefined || sizeInBytes === null) return 'N/A';
    
    const sizeInGB = sizeInBytes / (1024 * 1024 * 1024);
    
    if (sizeInGB < 1) {
      const sizeInMB = sizeInBytes / (1024 * 1024);
      return `${sizeInMB.toFixed(1)} MB`;
    }
    
    return `${sizeInGB.toFixed(1)} GB`;
  };

  // Calculate total size for group header
  const getTotalSize = useCallback((groupImages) => {
    return groupImages.reduce((total, img) => {
      return total + (img.size || 0);
    }, 0);
  }, []);

  // Render image row
  const renderImageRow = useCallback((image) => {
    const isExpanded = expandedImageId === image.id;

    return (
      <div key={image.id}>
        {/* Collapsed Row */}
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px 16px',
            borderBottom: '1px solid #222',
            borderLeft: '3px solid #6b7280',
            backgroundColor: isExpanded ? '#1a1a1a' : 'transparent',
            transition: 'background-color 0.15s'
          }}
          onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.backgroundColor = '#1a1a1a'; }}
          onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <div 
            onClick={() => handleToggleExpand(image.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              flex: 1,
              cursor: 'pointer',
              minWidth: 0
            }}
          >
            <span style={{
              color: '#fff',
              fontSize: '14px',
              fontWeight: '500',
              minWidth: '140px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {sanitizeText(image.name)}
            </span>
            <span style={{ color: '#9ca3af', fontSize: '12px' }}>
{image.size || 'N/A'}
            </span>
            <span style={{ color: '#6b7280', fontSize: '12px' }}>
              {formatDate(image.created_at || image.created)}
            </span>
          </div>
      
          
          {/* Expand chevron */}
          <div 
            onClick={() => handleToggleExpand(image.id)}
            style={{
              marginLeft: '12px',
              cursor: 'pointer',
              padding: '4px',
              color: '#6b7280',
              transition: 'transform 0.2s',
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
            }}
          >
            <FaChevronRight size={10} />
          </div>
        </div>
        
        {/* Expanded Details */}
        {isExpanded && (
          <div style={{
            padding: '16px',
            backgroundColor: '#0d0d0d',
            borderBottom: '1px solid #222'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: '16px'
            }}>
              {/* Details Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px'
              }}>
                <div>
                  <span style={{
                    fontSize: '10px',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    display: 'block',
                    marginBottom: '4px'
                  }}>Image ID</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
<span style={{ fontSize: '12px', color: '#fff' }}>{String(image.id)}</span>
                    <button 
                      onClick={() => copyToClipboard(image.id)}
                      style={{ 
                        padding: '2px', 
                        backgroundColor: 'transparent', 
                        border: 'none', 
                        cursor: 'pointer' 
                      }}
                    >
                      <FaCopy size={10} color={copiedId === image.id ? '#a5c77f' : '#6b7280'} />
                    </button>
                  </div>
                </div>
                <div>
                  <span style={{
                    fontSize: '10px',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    display: 'block',
                    marginBottom: '4px'
                  }}>Region</span>
                  <span style={{ fontSize: '12px', color: '#fff' }}>{image.region_name || 'N/A'}</span>
                </div>
                <div>
                  <span style={{
                    fontSize: '10px',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    display: 'block',
                    marginBottom: '4px'
                  }}>Created</span>
                  <span style={{ fontSize: '12px', color: '#fff' }}>
                    {formatDate(image.created_at || image.created)}
                  </span>
                </div>
                <div>
                  <span style={{
                    fontSize: '10px',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    display: 'block',
                    marginBottom: '4px'
                  }}>Snapshot Name</span>
                  <span style={{ fontSize: '12px', color: '#fff' }}>
                    {sanitizeText(image.snapshot_name) || 'N/A'}
                  </span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px'
              }}>
                <button
                  onClick={() => handleDeleteClick(image)}
                  disabled={loadingStates.deleting.has(image.id)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'center',
                    minWidth: '90px',
                    cursor: loadingStates.deleting.has(image.id) ? 'not-allowed' : 'pointer',
                    backgroundColor: 'rgba(238,85,85,0.1)',
                    color: '#ff7777',
                    border: '1px solid #ff7777',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => { if (!loadingStates.deleting.has(image.id)) e.currentTarget.style.backgroundColor = 'rgba(238,85,85,0.2)'; }}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(238,85,85,0.1)'}
                >
                  <FaTrash size={10} />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [expandedImageId, loadingStates.deleting, copiedId, handleToggleExpand, handleDeleteClick, copyToClipboard, formatDate, formatSize]);

  // Render image group
  const renderImageGroup = useCallback((groupName, groupImages) => {
    if (groupImages.length === 0) return null;
    
    const isCollapsed = collapsedGroups[groupName];
    const currentPage = groupPages[groupName] || 1;
    const totalPages = getGroupTotalPages(groupName);
    const paginatedImages = getPaginatedGroupImages(groupName);
    const totalSize = getTotalSize(groupImages);
    
    return (
      <div key={groupName}>
        {/* Group Header */}
        <div 
          onClick={() => toggleGroup(groupName)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            backgroundColor: '#1a1a1a',
            borderBottom: '1px solid #333',
            cursor: 'pointer',
            transition: 'background-color 0.15s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#222'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1a1a1a'}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: '#e0eacc',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            <FaChevronRight 
              size={10} 
              style={{ 
                transition: 'transform 0.2s',
                transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)'
              }}
            />
            {groupName}
            <span style={{
              backgroundColor: '#333',
              padding: '2px 8px',
              borderRadius: '9999px',
              fontSize: '12px',
              color: '#9ca3af',
              fontWeight: 'normal'
            }}>
              {groupImages.length}
            </span>
            {groupImages.length > GROUP_ITEMS_PER_PAGE && (
              <span style={{
                backgroundColor: 'rgba(224,234,204,0.1)',
                padding: '2px 8px',
                borderRadius: '9999px',
                fontSize: '10px',
                color: '#e0eacc'
              }}>
                {totalPages} pages
              </span>
            )}
          </div>
     <div style={{ fontSize: '12px', color: '#6b7280' }}>
  {groupImages.length} image{groupImages.length !== 1 ? 's' : ''}
</div>
        </div>
        
        {/* Group Content */}
        {!isCollapsed && (
          <>
            {paginatedImages.map(renderImageRow)}
            
            {/* Group Pagination */}
            <GroupPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => handleGroupPageChange(groupName, page)}
              totalItems={groupImages.length}
            />
          </>
        )}
      </div>
    );
  }, [collapsedGroups, groupPages, getGroupTotalPages, getPaginatedGroupImages, getTotalSize, toggleGroup, renderImageRow, handleGroupPageChange, formatSize]);

  // MOBILE/TABLET LAYOUT
  if (isMobile || isTablet) {
    return (
      <div style={{ display: 'flex', backgroundColor: '#000', minHeight: '100vh', color: '#fff' }}>
        <div style={{ flex: 1, padding: isMobile ? '16px' : '24px' }}>
          <div style={{ maxWidth: '1152px', margin: '0 auto' }}>
            {/* Header Section */}
            <div style={{
              marginBottom: '32px',
              backgroundColor: '#121212',
              border: '1px solid #333333',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
              padding: isMobile ? '24px' : '32px'
            }}>
              <div style={isMobile ? {} : { display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{
                    backgroundColor: '#222222',
                    border: '1px solid #333333',
                    borderRadius: '50%',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    marginRight: '24px',
                    padding: isMobile ? '12px' : '16px'
                  }}>
<Layers size={isMobile ? 20 : 40} style={{ color: '#e0eacc' }} strokeWidth={1.5} />                  </div>
                  <div>
                    <h1 style={{
                      fontWeight: '500',
                      color: '#fff',
                      fontSize: isMobile ? '24px' : '30px',
                      margin: 0
                    }}>Custom Images</h1>
                    <p style={{
                      color: '#9ca3af',
                      marginTop: '8px',
                      fontSize: isMobile ? '14px' : '16px'
                    }}>
                      Create and manage custom VM images for quick deployment
                    </p>
                  </div>
                </div>
                
                {/* Actions - Tablet Only */}
                {!isMobile && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                      onClick={() => {
                        logUserAction('create_image_modal_opened');
                        setCreateModalOpen(true);
                      }}
                      disabled={snapshots.length === 0 || loadingStates.creating}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontWeight: '500',
                        cursor: snapshots.length === 0 ? 'not-allowed' : 'pointer',
                        border: 'none',
                        backgroundColor: snapshots.length === 0 ? '#4a4a4a' : '#e0eacc',
                        color: snapshots.length === 0 ? '#9ca3af' : '#000'
                      }}
                    >
                      <FaPlus style={{ marginRight: '8px' }} /> Create Image
                    </button>
                    
                    <div style={{ position: 'relative' }} ref={regionDropdownRef}>
                      <div 
                        onClick={toggleRegionDropdown}
                        style={{
                          width: '144px',
                          padding: '12px',
                          backgroundColor: '#1d1d1d',
                          border: '1px solid #333333',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '14px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <span>{selectedRegion}</span>
                        <div style={{ display: 'flex', gap: '2px' }}>
                          {[0, 1, 2].map((i) => (
                            <div 
                              key={i}
                              style={{
                                width: '4px',
                                height: '4px',
                                backgroundColor: '#cad9e3',
                                borderRadius: '50%'
                              }}
                            />
                          ))}
                        </div>
                      </div>
                      
                      {isRegionDropdownOpen && (
                        <div style={{
                          position: 'absolute',
                          zIndex: 50,
                          left: 0,
                          right: 0,
                          marginTop: '4px',
                          backgroundColor: '#1d1d1d',
                          border: '1px solid #333333',
                          borderRadius: '6px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                          overflow: 'hidden'
                        }}>
                          <div style={{ padding: '4px 0' }}>
                            {regions.map((region) => (
                              <div 
                                key={region} 
                                onClick={() => handleRegionChange(region)}
                                style={{
                                  margin: '0 8px',
                                  padding: '6px 8px',
                                  cursor: 'pointer',
                                  borderRadius: '4px',
                                  backgroundColor: region === selectedRegion ? '#252525' : 'transparent',
                                  color: region === selectedRegion ? '#fff' : '#e5e5e5'
                                }}
                              >
                                {region}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={handleRefresh}
                      disabled={loadingStates.images}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 12px',
                        backgroundColor: '#333333',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        color: '#fff'
                      }}
                    >
                      <FaChevronRight size={14} style={{ marginRight: '8px', transform: 'rotate(90deg)' }} />
                      Refresh
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile Actions */}
              {isMobile && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                    <button
                      onClick={() => {
                        logUserAction('create_image_modal_opened');
                        setCreateModalOpen(true);
                      }}
                      disabled={snapshots.length === 0 || loadingStates.creating}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '12px 16px',
                        borderRadius: '6px',
                        fontWeight: '500',
                        cursor: snapshots.length === 0 ? 'not-allowed' : 'pointer',
                        border: 'none',
                        backgroundColor: snapshots.length === 0 ? '#4a4a4a' : '#e0eacc',
                        color: snapshots.length === 0 ? '#9ca3af' : '#000'
                      }}
                    >
                      <FaPlus size={14} style={{ marginRight: '8px' }} /> Create Image
                    </button>
                    
                    <button
                      onClick={handleRefresh}
                      disabled={loadingStates.images}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '12px 16px',
                        backgroundColor: '#333333',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        color: '#fff'
                      }}
                    >
                      Refresh
                    </button>
                  </div>
                  
                  <div style={{ position: 'relative' }} ref={regionDropdownRef}>
                    <div 
                      onClick={toggleRegionDropdown}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: '#1d1d1d',
                        border: '1px solid #333333',
                        borderRadius: '6px',
                        color: '#fff',
                        fontSize: '14px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <span>Region: {selectedRegion}</span>
                      <div style={{ display: 'flex', gap: '2px' }}>
                        {[0, 1, 2].map((i) => (
                          <div 
                            key={i}
                            style={{
                              width: '4px',
                              height: '4px',
                              backgroundColor: '#cad9e3',
                              borderRadius: '50%'
                            }}
                          />
                        ))}
                      </div>
                    </div>
                    
                    {isRegionDropdownOpen && (
                      <div style={{
                        position: 'absolute',
                        zIndex: 50,
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        backgroundColor: '#1d1d1d',
                        border: '1px solid #333333',
                        borderRadius: '6px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                        overflow: 'hidden'
                      }}>
                        <div style={{ padding: '4px 0' }}>
                          {regions.map((region) => (
                            <div 
                              key={region} 
                              onClick={() => handleRegionChange(region)}
                              style={{
                                margin: '0 8px',
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                backgroundColor: region === selectedRegion ? '#252525' : 'transparent',
                                color: region === selectedRegion ? '#fff' : '#e5e5e5'
                              }}
                            >
                              {region}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Search and Sort */}
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '6px',
                padding: '8px 12px',
                flex: 1,
                maxWidth: '300px'
              }}>
                <FaSearch size={14} color="#6b7280" />
                <input
  type="text"
  placeholder="Search images..."
  value={searchQuery}
  onChange={(e) => setSearchQuery(e.target.value)}
  maxLength={30}
  style={{
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#fff',
    marginLeft: '8px',
    fontSize: '14px',
    width: '100%'
  }}
/>
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <FaTimes size={12} color="#6b7280" />
                  </button>
                )}
              </div>
              
              <div style={{ position: 'relative' }} ref={sortDropdownRef}>
                <button
                  onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    color: '#9ca3af',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <FaSort size={12} />
                  {sortOptions.find(s => s.id === sortBy)?.label}
                </button>
                {sortDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '4px',
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    zIndex: 50,
                    minWidth: '100px'
                  }}>
                    {sortOptions.map(s => (
                      <div
                        key={s.id}
                        onClick={() => { setSortBy(s.id); setSortDropdownOpen(false); }}
                        style={{
                          padding: '8px 12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          color: sortBy === s.id ? '#e0eacc' : '#9ca3af',
                          backgroundColor: sortBy === s.id ? 'rgba(224,234,204,0.1)' : 'transparent'
                        }}
                      >
                        {s.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div style={{ marginLeft: 'auto', color: '#6b7280', fontSize: '12px' }}>
                {filteredImages.length} image{filteredImages.length !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Loading State */}
            {loadingStates.images ? (
              <div style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #333333',
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: isMobile ? '32px' : '48px'
              }}>
                <div style={{
                  width: isMobile ? '32px' : '48px',
                  height: isMobile ? '32px' : '48px',
                  border: '4px solid rgba(224,234,204,0.2)',
                  borderTopColor: '#e0eacc',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ color: '#9ca3af', marginLeft: '16px', fontSize: isMobile ? '14px' : '16px' }}>Loading images...</p>
              </div>
            ) : filteredImages.length === 0 ? (
              /* Empty State */
              <div style={{
                backgroundColor: '#121212',
                borderRadius: '12px',
                border: '1px solid #333333',
                padding: isMobile ? '24px' : '32px'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
padding: '70px 0'                }}>
                  <div style={{ marginBottom: '24px' }}>
<Layers size={48} style={{ color: '#e0eacc' }} strokeWidth={1.5} />                  </div>
                  <h3 style={{
                    fontSize: isMobile ? '18px' : '20px',
                    fontWeight: '600',
                    color: '#fff',
                    marginBottom: '8px'
                  }}>No Images Found</h3>
                  <p style={{
                    color: '#9ca3af',
                    textAlign: 'center',
                    maxWidth: '400px',
                    fontSize: isMobile ? '14px' : '16px'
                  }}>
                    {searchQuery 
                      ? `No images matching "${searchQuery}" in ${selectedRegion}`
                      : `You haven't created any images in the ${selectedRegion} region yet.`
                    }
                  </p>
                </div>
              </div>
            ) : (
              /* Image Groups List */
              <div style={{
                backgroundColor: '#121212',
                borderRadius: '10px',
                border: '1px solid #333',
                overflow: 'hidden'
              }}>
                {renderImageGroup('All Images', groupedImages['All Images'])}
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        <CreateImageModal
          isOpen={createModalOpen}
          onClose={() => {
            setCreateModalOpen(false);
            logUserAction('create_image_modal_closed');
          }}
          onSubmit={handleCreateImage}
          snapshots={snapshots}
        />

        
        <DeleteConfirmationModal
          isOpen={deleteModal.open}
          onClose={() => {
            setDeleteModal({ open: false, image: null });
            logUserAction('delete_image_modal_closed');
          }}
          onConfirm={handleDeleteConfirm}
          itemName={deleteModal.image ? deleteModal.image.name : ''}
          isDeleting={isDeleting}
        />

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // DESKTOP LAYOUT
  return (
    <div style={{ display: 'flex', backgroundColor: '#000', minHeight: '100vh', color: '#fff' }}>
      <Sidebar 
        activePage="images" 
        isMobile={isMobile}
        isTablet={isTablet}
        isSidebarOpen={false}
        toggleSidebar={() => {}}
        authenticated={true}
        onAuthAction={() => {}}
      />
      
<div style={{ flex: 1, overflowX: 'hidden' }}>
        <div style={{ width: '100%', maxWidth: '1600px', margin: '0 auto', padding: '0 32px' }}>
                    {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 0',
            borderBottom: '1px solid #333333'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ marginRight: '16px' }}>
<Layers size={20} style={{ color: '#e0eacc' }} strokeWidth={1.5} />              </div>
              <div>
                <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#fff', margin: 0 }}>Custom Images</h1>
                <p style={{ color: '#9ca3af', fontSize: '14px', margin: '4px 0 0 0' }}>Create and manage custom VM images for quick deployment</p>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  logUserAction('create_image_modal_opened');
                  setCreateModalOpen(true);
                }}
                disabled={snapshots.length === 0 || loadingStates.creating}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontWeight: '500',
                  cursor: snapshots.length === 0 ? 'not-allowed' : 'pointer',
                  border: 'none',
                  backgroundColor: snapshots.length === 0 ? '#4a4a4a' : '#e0eacc',
                  color: snapshots.length === 0 ? '#9ca3af' : '#000'
                }}
              >
                <FaPlus style={{ marginRight: '8px' }} /> Create Image
              </button>
              
              <div style={{ position: 'relative' }} ref={regionDropdownRef}>
                <div 
                  onClick={toggleRegionDropdown}
                  style={{
                    width: '144px',
                    padding: '12px',
                    backgroundColor: '#1d1d1d',
                    border: '1px solid #333333',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '14px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <span>{selectedRegion}</span>
                  <div style={{ display: 'flex', gap: '2px' }}>
                    {[0, 1, 2].map((i) => (
                      <div 
                        key={i}
                        style={{
                          width: '4px',
                          height: '4px',
                          backgroundColor: '#cad9e3',
                          borderRadius: '50%'
                        }}
                      />
                    ))}
                  </div>
                </div>
                
                {isRegionDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    zIndex: 50,
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: '#1d1d1d',
                    border: '1px solid #333333',
                    borderRadius: '6px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                    overflow: 'hidden'
                  }}>
                    <div style={{ padding: '4px 0' }}>
                      {regions.map((region) => (
                        <div 
                          key={region} 
                          onClick={() => handleRegionChange(region)}
                          style={{
                            margin: '0 8px',
                            padding: '6px 8px',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            backgroundColor: region === selectedRegion ? '#252525' : 'transparent',
                            color: region === selectedRegion ? '#fff' : '#e5e5e5'
                          }}
                        >
                          {region}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={handleRefresh}
                disabled={loadingStates.images}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: '#333333',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  color: '#fff',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  backgroundColor: '#cad9e3',
                  transition: 'width 0.3s',
                  borderRadius: '6px',
                  width: loadingStates.images ? '100%' : '0%'
                }}></div>
                <div style={{ position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center' }}>
                  <svg 
                    style={{ marginRight: '8px', width: '16px', height: '16px', animation: loadingStates.images ? 'spin 1s linear infinite' : 'none' }}
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span style={{ color: loadingStates.images ? '#000' : '#fff' }}>Refresh</span>
                </div>
              </button>
            </div>
          </div>
          
          <div style={{ padding: '24px 0', minHeight: '120vh' }}>
            {/* Search and Sort Bar */}
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '6px',
                padding: '8px 12px',
                flex: 1,
                maxWidth: '300px'
              }}>
                <FaSearch size={14} color="#6b7280" />
               <input
  type="text"
  placeholder="Search images..."
  value={searchQuery}
  onChange={(e) => setSearchQuery(e.target.value)}
  maxLength={30}
  style={{
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#fff',
    marginLeft: '8px',
    fontSize: '14px',
    width: '100%'
  }}
/>
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <FaTimes size={12} color="#6b7280" />
                  </button>
                )}
              </div>
              
              <div style={{ position: 'relative' }} ref={sortDropdownRef}>
                <button
                  onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    color: '#9ca3af',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <FaSort size={12} />
                  {sortOptions.find(s => s.id === sortBy)?.label}
                </button>
                {sortDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '4px',
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    zIndex: 50,
                    minWidth: '100px'
                  }}>
                    {sortOptions.map(s => (
                      <div
                        key={s.id}
                        onClick={() => { setSortBy(s.id); setSortDropdownOpen(false); }}
                        style={{
                          padding: '8px 12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          color: sortBy === s.id ? '#e0eacc' : '#9ca3af',
                          backgroundColor: sortBy === s.id ? 'rgba(224,234,204,0.1)' : 'transparent'
                        }}
                      >
                        {s.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div style={{ marginLeft: 'auto', color: '#6b7280', fontSize: '12px' }}>
                {filteredImages.length} image{filteredImages.length !== 1 ? 's' : ''}
              </div>
            </div>
            
            {/* Loading State */}
            {loadingStates.images ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '64px 0'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid rgba(224,234,204,0.2)',
                  borderTopColor: '#e0eacc',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ color: '#9ca3af', marginLeft: '16px' }}>Loading images...</p>
              </div>
            ) : filteredImages.length === 0 ? (
              /* Empty State */
              <div style={{
                backgroundColor: '#121212',
                borderRadius: '10px',
                padding: '32px',
                border: '1px solid #333333',
                boxShadow: '0 6px 24px rgba(0,0,0,0.4)'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '90px 0'
                }}>
                  <div style={{ marginBottom: '24px' }}>
<Layers size={48} style={{ color: '#e0eacc' }} strokeWidth={1.5} />
              </div>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#fff', marginBottom: '8px' }}>No Images Found</h3>
                  <p style={{ color: '#9ca3af', textAlign: 'center', maxWidth: '400px' }}>
                    {searchQuery 
                      ? `No images matching "${searchQuery}" in ${selectedRegion}`
                      : `You haven't created any images in the ${selectedRegion} region yet.`
                    }
                  </p>
                </div>
              </div>
            ) : (
              /* Image Groups List */
              <div style={{
                backgroundColor: '#121212',
                borderRadius: '10px',
                border: '1px solid #333',
                overflow: 'hidden'
              }}>
                {renderImageGroup('All Images', groupedImages['All Images'])}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modals */}
      <CreateImageModal
        isOpen={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          logUserAction('create_image_modal_closed');
        }}
        onSubmit={handleCreateImage}
        snapshots={snapshots}
      />

    
      
      <DeleteConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => {
          setDeleteModal({ open: false, image: null });
          logUserAction('delete_image_modal_closed');
        }}
        onConfirm={handleDeleteConfirm}
        itemName={deleteModal.image ? deleteModal.image.name : ''}
        isDeleting={isDeleting}
      />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ImagePage;